import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Snackbar,
    Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";

interface User {
    id: number;
    username: string;
}

const Accounts: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [newUsername, setNewUsername] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [editingUsername, setEditingUsername] = useState("");
    const [editingPassword, setEditingPassword] = useState("");
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info" | "warning";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    const fetchUsers = async () => {
        try {
            const response = await axios.get("/auth/users");
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const handleCreateUser = async () => {
        try {
            await axios.post("/auth/register", {
                username: newUsername,
                password: newPassword || "defaultPassword",
            });
            setSnackbar({
                open: true,
                message: "User created successfully!",
                severity: "success",
            });
            setNewUsername("");
            setNewPassword("");
            fetchUsers();
        } catch (error) {
            console.error("Error creating user:", error);
            setSnackbar({
                open: true,
                message: "Failed to create user.",
                severity: "error",
            });
        }
    };

    const handleUpdateUser = async (id: number) => {
        try {
            await axios.put(`/auth/users/${id}`, {
                username: editingUsername,
                password: editingPassword || undefined,
            });
            setSnackbar({
                open: true,
                message: "User updated successfully!",
                severity: "success",
            });
            setEditingUserId(null);
            setEditingPassword("");
            fetchUsers();
        } catch (error) {
            console.error("Error updating user:", error);
            setSnackbar({
                open: true,
                message: "Failed to update user.",
                severity: "error",
            });
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Manage Accounts
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                    label="New Username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    fullWidth
                />
                <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateUser}>
                    Create
                </Button>
            </Box>

            <List>
                {users.map((user) => (
                    <ListItem key={user.id} divider>
                        {editingUserId === user.id ? (
                            <>
                                <TextField
                                    label="Edit Username"
                                    value={editingUsername}
                                    onChange={(e) =>
                                        setEditingUsername(e.target.value)
                                    }
                                    fullWidth
                                />
                                <TextField
                                    label="Edit Password"
                                    type="password"
                                    value={editingPassword}
                                    onChange={(e) =>
                                        setEditingPassword(e.target.value)
                                    }
                                    fullWidth
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        onClick={() =>
                                            handleUpdateUser(user.id)
                                        }>
                                        <SaveIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </>
                        ) : (
                            <>
                                <ListItemText primary={user.username} />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        onClick={() => {
                                            setEditingUserId(user.id);
                                            setEditingUsername(user.username);
                                        }}>
                                        <EditIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </>
                        )}
                    </ListItem>
                ))}
            </List>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Accounts;
